import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DropResult } from '@hello-pangea/dnd';
import { useProductionPlanning } from 'context/production-planning-context';
import { AutoScheduleDirection, ProductionPlanningStep } from 'models/production-planning';
import { StepOption } from 'components/shared/add-step-autocomplete/add-step-autocomplete.component';

type UseProductionSchedulingLogicProps = {
    steps: ProductionPlanningStep[];
    onStepsChange: (steps: ProductionPlanningStep[]) => void;
    internalDeliveryDate?: Date | null;
    onValidChange?: (isValid: boolean) => void;
    disablePastDateWarnings?: boolean;
};

export const useProductionSchedulingLogic = ({
    steps,
    onStepsChange,
    internalDeliveryDate,
    onValidChange,
    disablePastDateWarnings = false,
}: UseProductionSchedulingLogicProps) => {
    const { t: translate } = useTranslation(['orders']);
    const {
        runFullAutoSchedule: runAutoSchedule,
        handleStepChangeWithCascade,
        validateStepsChronological,
        validateDeadline,
        validateStartDate,
        autoScheduling,
    } = useProductionPlanning();

    const [direction, setDirection] = useState<AutoScheduleDirection>(AutoScheduleDirection.FORWARD_FROM_NOW);
    const [startDate, setStartDate] = useState<Date | null>(() => {
        const d = new Date();
        d.setHours(d.getHours() + 1, 0, 0, 0);
        return d;
    });
    const [invalidTimeInputs, setInvalidTimeInputs] = useState<Set<string>>(new Set());

    const stepsRef = useRef(steps);
    stepsRef.current = steps;

    // ============================================================================
    // Auto-Schedule Logic
    // ============================================================================

    const runFullAutoSchedule = useCallback(async () => {
        try {
            const updatedSteps = await runAutoSchedule(steps, direction, startDate, internalDeliveryDate);
            onStepsChange(updatedSteps);
        } catch (error) {
            console.error('Error auto-scheduling:', error);
        }
    }, [runAutoSchedule, steps, direction, startDate, internalDeliveryDate, onStepsChange]);

    // Auto-schedule on initial load when template is applied
    const hasAutoScheduledOnLoad = useRef(false);
    useEffect(() => {
        if (steps.length === 0) {
            hasAutoScheduledOnLoad.current = false;
            return;
        }
        if (hasAutoScheduledOnLoad.current) return;
        const hasHours = steps.some((s) => s.estimatedProductionHours && s.estimatedProductionHours > 0);
        const hasNoDeadlines = steps.every((s) => !s.deadline);
        if (hasHours && hasNoDeadlines) {
            hasAutoScheduledOnLoad.current = true;
            runFullAutoSchedule();
        }
    }, [steps, runFullAutoSchedule]);

    // ============================================================================
    // Step Handlers
    // ============================================================================

    const handleStepChange = async (index: number, update: Partial<ProductionPlanningStep>) => {
        try {
            const updatedSteps = await handleStepChangeWithCascade(steps, index, update);
            onStepsChange(updatedSteps);
        } catch (error) {
            console.error('Error updating step with cascade:', error);
        }
    };

    const handleDeleteStep = (index: number) => {
        onStepsChange(steps.filter((_, i) => i !== index));
    };

    const handleAddStep = (step: StepOption) => {
        const newStep: ProductionPlanningStep = {
            stepId: step.id,
            stepName: step.name,
            technicianId: '',
            technicianName: '',
            startDate: null,
            deadline: null,
            estimatedProductionHours: undefined,
            estimatedMachineHours: undefined,
            stepOrder: undefined,
            allowOvernight: false,
            startNextDay: false,
            affectedByQuantityModifier: 0,
        };
        onStepsChange([...steps, newStep]);
    };

    const handleDragEnd = (result: DropResult) => {
        if (!result.destination) return;
        const reordered = [...steps];
        const [removed] = reordered.splice(result.source.index, 1);
        reordered.splice(result.destination.index, 0, removed);
        onStepsChange(reordered);
    };

    const handleMoveStepToNextDay = async (index: number) => {
        const step = steps[index];
        if (!step) return;

        if (!step.startDate) return;
        const nextDay = new Date(step.startDate);
        nextDay.setDate(nextDay.getDate() + 1);
        nextDay.setHours(8, 0, 0, 0);

        // Update startDate and clear deadline - cascade will recalculate deadline based on new startDate
        // Clearing deadline prevents validation error when startDate moves past old deadline
        await handleStepChange(index, { startDate: nextDay, deadline: null });
    };

    // ============================================================================
    // Time Input Validation
    // ============================================================================

    const handleTimeInputValidChange = useCallback((index: number, field: 'production' | 'idle', isValid: boolean) => {
        const key = `${index}-${field}`;
        setInvalidTimeInputs((prev) => {
            const next = new Set(prev);
            if (isValid) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    }, []);

    const isTimeInputInvalid = useCallback(
        (index: number, field: 'production' | 'idle'): boolean => {
            const key = `${index}-${field}`;
            return invalidTimeInputs.has(key);
        },
        [invalidTimeInputs],
    );

    // ============================================================================
    // Validation
    // ============================================================================

    const stepsNotChronological = useMemo(
        () => !validateStepsChronological(steps),
        [steps, validateStepsChronological],
    );

    const isDeadlineInvalid = useCallback(
        (deadline: Date | null): boolean => {
            if (disablePastDateWarnings) return false;
            return !validateDeadline(deadline, internalDeliveryDate);
        },
        [validateDeadline, internalDeliveryDate, disablePastDateWarnings],
    );

    const isStartDateInvalid = useCallback(
        (step: ProductionPlanningStep, index: number): boolean => {
            if (disablePastDateWarnings) return false;
            return !validateStartDate(step, index);
        },
        [validateStartDate, disablePastDateWarnings],
    );

    const warnings = useMemo(() => {
        const result: string[] = [];
        const stepsWithDeadlines = steps.filter((s) => s.deadline);

        if (stepsWithDeadlines.length > 0) {
            if (internalDeliveryDate) {
                const lastDeadline = new Date(stepsWithDeadlines[stepsWithDeadlines.length - 1].deadline!);
                const deadline = new Date(internalDeliveryDate);
                deadline.setHours(23, 59, 59, 999);
                if (lastDeadline > deadline) {
                    result.push(translate('SCHEDULE_WARNING_EXCEEDS_DEADLINE'));
                }
            }

            const firstDeadline = new Date(stepsWithDeadlines[0].deadline!);
            if (!disablePastDateWarnings && firstDeadline < new Date()) {
                result.push(translate('SCHEDULE_WARNING_STARTS_IN_PAST'));
            }
        }

        return result;
    }, [steps, internalDeliveryDate, translate]);

    const errors = useMemo(() => {
        const result: string[] = [];
        if (stepsNotChronological) {
            result.push(translate('SCHEDULE_WARNING_NOT_CHRONOLOGICAL'));
        }
        if (invalidTimeInputs.size > 0) {
            result.push(translate('SCHEDULE_WARNING_INVALID_TIME_FORMAT'));
        }
        return result;
    }, [stepsNotChronological, invalidTimeInputs.size, translate]);

    const isValid = errors.length === 0;

    const prevIsValid = useRef(isValid);
    useEffect(() => {
        if (prevIsValid.current !== isValid) {
            prevIsValid.current = isValid;
            onValidChange?.(isValid);
        }
    }, [isValid, onValidChange]);

    return {
        direction,
        setDirection,
        startDate,
        setStartDate,
        autoScheduling,
        runFullAutoSchedule,
        handleStepChange,
        handleDeleteStep,
        handleAddStep,
        handleDragEnd,
        handleMoveStepToNextDay,
        handleTimeInputValidChange,
        isTimeInputInvalid,
        isDeadlineInvalid,
        isStartDateInvalid,
        warnings,
        errors,
    };
};
